// src/features/merchantConfig/components/ArrayTable.jsx
//
// Renders an array of (heterogeneous) objects as a sortable table whose
// columns are the UNION of keys across all items. Rows are clickable to
// expand into a per-row full-fields view (with FieldRow handling per-cell
// editing in edit mode).
//
// Edit-mode additions:
//   * "Add row" button below the table - clones the union-of-keys schema with
//     `null` values for each column and appends it to the draft array.
//   * Per-row "Delete" button next to the row toggle. Removes the row from
//     the draft array.
//   * Because the OMS API replaces arrays wholesale (API doc §5.5), every
//     mutation in edit mode sends the entire updated array up via the parent
//     `onCommit` (no diff). The patch body builder will then include this
//     full array.
//
// Adding new ARBITRARY KEYS to a row is intentionally not exposed in the UI
// - the typed value-block class on the server is the source of truth, so
// the merchant can only edit fields that already exist on at least one
// item.

import React, { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Plus,
  Trash2,
} from "lucide-react";
import { humanizeLabel } from "../lib/humanizeLabel";
import FieldRow from "./FieldRow";
import { useEditState } from "../state/EditStateContext";
import { isFieldEditable } from "../lib/editFieldPermissions";

/**
 * Derive the union of object keys across all items. Stable order: keys are
 * yielded in first-seen order, which keeps the table layout intuitive.
 *
 * @param {Array<Record<string, unknown>>} items
 * @returns {string[]}
 */
function unionKeys(items) {
  const seen = new Set();
  const order = [];
  for (const item of items) {
    if (item && typeof item === "object" && !Array.isArray(item)) {
      for (const k of Object.keys(item)) {
        if (!seen.has(k)) {
          seen.add(k);
          order.push(k);
        }
      }
    }
  }
  return order;
}

function compactCellPreview(v) {
  if (v === null || v === undefined) return "-";
  if (typeof v === "boolean") return v ? "Enabled" : "Disabled";
  if (typeof v === "number") return v.toLocaleString();
  if (typeof v === "string") {
    if (v.length > 60) return v.slice(0, 59) + "…";
    return v;
  }
  if (Array.isArray(v)) {
    if (v.length === 0) return "[]";
    if (v.every((x) => typeof x === "string" || typeof x === "number")) {
      const preview = v.slice(0, 3).join(", ");
      return v.length > 3 ? `${preview}, +${v.length - 3} more` : preview;
    }
    return `${v.length} item${v.length === 1 ? "" : "s"}`;
  }
  if (typeof v === "object") return "{…}";
  return String(v);
}

function compareValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") {
    return a === b ? 0 : a ? 1 : -1;
  }
  return String(a).localeCompare(String(b));
}

function useEditStateOptional() {
  try {
    return useEditState();
  } catch {
    return null;
  }
}

/**
 * @param {{
 *   fieldKey: string,
 *   items: Array<Record<string, unknown>>,
 *   path: Array<string | number>,
 *   readOnly?: boolean,
 *   onEdit?: (path: Array<string | number>, newValue: unknown) => void,
 *   configKey?: string,
 * }} props
 */
export default function ArrayTable({
  fieldKey,
  items,
  path,
  readOnly = true,
  onEdit,
  configKey,
}) {
  const editState = useEditStateOptional();
  const inEditMode = !readOnly && editState?.mode === "edit";
  // Array-level mutations (Add row / Delete row) ride on the same per-field
  // policy as scalar edits - if the array path itself is blocked (e.g. a
  // cost-bearing table inside SMS Notifications), we hide the affordances
  // and bail out of handlers defensively.
  const arrayMutationsAllowed =
    inEditMode && isFieldEditable({ configKey: configKey || "", path });

  const columns = useMemo(() => unionKeys(items), [items]);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [expandedRow, setExpandedRow] = useState(null);

  const sortedIndices = useMemo(() => {
    const indices = items.map((_, i) => i);
    if (!sortBy) return indices;
    indices.sort((ai, bi) => {
      const av = items[ai] ? items[ai][sortBy] : undefined;
      const bv = items[bi] ? items[bi][sortBy] : undefined;
      const cmp = compareValues(av, bv);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return indices;
  }, [items, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const handleAddRow = () => {
    if (!editState) return;
    if (!arrayMutationsAllowed) return;
    // Clone the union-of-keys schema with null values so the new row has
    // a placeholder slot in every column.
    const newRow = {};
    for (const col of columns) {
      newRow[col] = null;
    }
    const next = [...items, newRow];
    if (onEdit) onEdit(path, next);
    editState.updateField(path, next);
    setExpandedRow(items.length); // expand the new row by default
  };

  const handleDeleteRow = (origIdx) => {
    if (!editState) return;
    if (!arrayMutationsAllowed) return;
    const next = items.slice();
    next.splice(origIdx, 1);
    if (onEdit) onEdit(path, next);
    editState.updateField(path, next);
    setExpandedRow((cur) => (cur === origIdx ? null : cur));
  };

  if (!items.length && !arrayMutationsAllowed) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/60 px-4 py-3 text-sm text-gray-500">
        <span className="font-medium text-gray-700">{humanizeLabel(fieldKey)}</span>{" "}
        - no items
      </div>
    );
  }

  return (
    <section
      aria-label={humanizeLabel(fieldKey)}
      className="overflow-hidden rounded-lg border border-gray-200 bg-white"
      data-readonly={readOnly ? "true" : "false"}
    >
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2.5">
        <span className="text-sm font-semibold text-gray-900">
          {humanizeLabel(fieldKey)}
        </span>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-gray-200">
            {items.length} {items.length === 1 ? "item" : "items"}
          </span>
          {arrayMutationsAllowed && (
            <button
              type="button"
              onClick={handleAddRow}
              className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Add row
            </button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-gray-500">
          No items yet - click <span className="font-medium">Add row</span> to
          create one.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="w-8 px-2 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                >
                  <span className="sr-only">Row toggle</span>
                </th>
                {columns.map((col) => {
                  const active = sortBy === col;
                  return (
                    <th
                      key={col}
                      scope="col"
                      className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                      aria-sort={
                        active
                          ? sortDir === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                    >
                      <button
                        type="button"
                        onClick={() => toggleSort(col)}
                        className="inline-flex items-center gap-1 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      >
                        {humanizeLabel(col)}
                        {active ? (
                          sortDir === "asc" ? (
                            <ChevronUp className="h-3 w-3" aria-hidden="true" />
                          ) : (
                            <ChevronDown className="h-3 w-3" aria-hidden="true" />
                          )
                        ) : (
                          <ChevronsUpDown
                            className="h-3 w-3 text-gray-300"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    </th>
                  );
                })}
                {arrayMutationsAllowed && (
                  <th
                    scope="col"
                    className="w-10 px-2 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500"
                  >
                    <span className="sr-only">Row actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {sortedIndices.map((origIdx) => {
                const row = items[origIdx];
                const isOpen = expandedRow === origIdx;
                return (
                  <React.Fragment key={origIdx}>
                    <tr
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() =>
                        setExpandedRow((cur) =>
                          cur === origIdx ? null : origIdx
                        )
                      }
                    >
                      <td className="w-8 px-2 py-2 align-top text-gray-400">
                        {isOpen ? (
                          <ChevronDown className="h-4 w-4" aria-hidden="true" />
                        ) : (
                          <ChevronRightIcon />
                        )}
                      </td>
                      {columns.map((col) => (
                        <td
                          key={col}
                          className="px-3 py-2 align-top text-gray-900"
                        >
                          {row && col in row ? (
                            <span className="break-words">
                              {compactCellPreview(row[col])}
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      ))}
                      {arrayMutationsAllowed && (
                        <td className="w-10 px-2 py-2 text-right align-top">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteRow(origIdx);
                            }}
                            aria-label="Delete row"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                        </td>
                      )}
                    </tr>
                    {isOpen && row && (
                      <tr>
                        <td
                          colSpan={
                            columns.length + (arrayMutationsAllowed ? 2 : 1)
                          }
                          className="bg-blue-50/30 px-4 py-3"
                        >
                          <dl className="space-y-1">
                            {/* In edit mode iterate the UNION of columns so
                                sparse rows get a slot for every field. In
                                view mode iterate only the keys that actually
                                exist on the row (matches Phase 1 behavior). */}
                            {(inEditMode ? columns : Object.keys(row)).map(
                              (k) => (
                                <FieldRow
                                  key={k}
                                  fieldKey={k}
                                  value={row[k]}
                                  path={[...path, origIdx, k]}
                                  readOnly={readOnly}
                                  onEdit={onEdit}
                                  configKey={configKey}
                                />
                              )
                            )}
                          </dl>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
